package link.erpango.quip.domain.noteimport

import link.erpango.quip.QuipConstants
import link.erpango.quip.domain.entities.VehicleType
import link.erpango.quip.domain.extraction.ExtractionConfidence
import link.erpango.quip.domain.extraction.normalizeText

/**
 * Turns values as written in a note (or returned by the LLM) into the exact values the
 * creation forms use: catalog spellings, dropdown options and color ids. Every function
 * returns null when it can not map the value, so nothing unexpected reaches a form.
 */

/** "4521-klm" / "4521 KLM" -> "4521KLM". */
fun normalizePlate(raw: String): String = raw.filterNot { it.isWhitespace() || it == '-' }.uppercase()

/** "vf1-ttgfp0 vst12345" -> "VF1TTGFP0VST12345". */
fun normalizeVin(raw: String): String = raw.filterNot { it.isWhitespace() || it == '-' }.uppercase()

/** ISO 3779: 17 characters without I, O or Q. */
fun isWellFormedVin(vin: String): Boolean = VIN_FORMAT.matches(vin)

private val VIN_FORMAT = Regex("^[A-HJ-NPR-Z0-9]{17}$")

/** The catalog spelling of [raw] ("seat" -> "SEAT", "citroen" -> "Citroën"), ignoring case and accents. */
fun canonicalName(raw: String, options: List<String>): String? {
    val key = foldForMatch(raw)
    if (key.isEmpty()) return null
    return options.firstOrNull { foldForMatch(it) == key }
}

/** Diacritics beyond Spanish found in catalog names (Citroën, Škoda...). */
private val EXTRA_DIACRITICS = mapOf(
    'à' to 'a', 'â' to 'a', 'ä' to 'a', 'å' to 'a', 'ã' to 'a', 'è' to 'e', 'ê' to 'e', 'ë' to 'e',
    'ì' to 'i', 'î' to 'i', 'ï' to 'i', 'ò' to 'o', 'ô' to 'o', 'ö' to 'o', 'õ' to 'o', 'ø' to 'o',
    'ù' to 'u', 'û' to 'u', 'ç' to 'c', 'š' to 's', 'č' to 'c', 'ž' to 'z', 'ř' to 'r', 'ý' to 'y',
)

/** Lowercase, trimmed, without accents: only for comparing names, never for spans. */
internal fun foldForMatch(text: String): String =
    normalizeText(text.trim()).map { EXTRA_DIACRITICS[it] ?: it }.joinToString("")

private val OIL_VISCOSITY_REGEX = Regex("(\\d{1,2})\\s*w\\s*-?\\s*(\\d{2,3})")

/** "5w30", "Castrol EDGE 5W-30" -> "5W-30" (an option of the engine oil dropdown). */
fun canonicalOilViscosity(raw: String): String? {
    val match = OIL_VISCOSITY_REGEX.find(normalizeText(raw)) ?: return null
    return "${match.groupValues[1]}W-${match.groupValues[2]}"
}

private val TIRE_SIZE_REGEX = Regex("(\\d{3})\\s*/\\s*(\\d{2})\\s*z?r\\s*(\\d{2})")

/** "205/55 R16" -> "205/55R16", the format of the tire size dropdown. */
fun normalizeTireSize(raw: String): String? {
    val match = TIRE_SIZE_REGEX.find(normalizeText(raw)) ?: return null
    val (width, profile, rim) = match.destructured
    return "$width/${profile}R$rim"
}

/** A fuel dropdown option and how sure the mapping is ("gasolina" could be 95 or 98). */
data class FuelOption(val option: String, val confidence: ExtractionConfidence)

/** Maps free text to one of [QuipConstants.VehicleAttributes.FuelTypes]. */
fun fuelOptionFromText(raw: String): FuelOption? {
    val text = normalizeText(raw)
    fun option(value: String, confidence: ExtractionConfidence = ExtractionConfidence.HIGH): FuelOption? =
        FuelOption(value, confidence).takeIf { value in QuipConstants.VehicleAttributes.FuelTypes }
    return when {
        "enchufable" in text || "phev" in text -> option("Híbrido Enchufable")
        "hibrid" in text -> option("Híbrido")
        "electric" in text -> option("Eléctrico")
        "glp" in text || "gpl" in text || "autogas" in text -> option("GLP")
        "gnc" in text || "cng" in text || "gas natural" in text -> option("GNC")
        ("diesel" in text || "gasoil" in text || "gasoleo" in text) && "premium" in text -> option("Diésel Premium")
        "diesel" in text || "gasoil" in text || "gasoleo" in text || "tdi" in text -> option("Diésel")
        "98" in text -> option("Gasolina 98")
        "95" in text -> option("Gasolina 95")
        // "gasolina" alone: 95 is the usual pump, but the user has to confirm it.
        "gasolina" in text || "nafta" in text -> option("Gasolina 95", ExtractionConfidence.LOW)
        else -> null
    }
}

fun vehicleTypeFromCode(code: String): VehicleType? =
    VehicleType.entries.firstOrNull { it.code == code.trim().lowercase() }

/**
 * Spanish and English color words -> VehicleColors id. Longest phrases first, so
 * "azul marino" wins over "azul".
 */
private val COLOR_WORDS: List<Pair<String, String>> = listOf(
    "blanco perla" to "white_pearl", "gris oscuro" to "grey_dark", "gris marengo" to "grey_dark",
    "azul marino" to "blue_navy", "azul claro" to "blue_light", "azul oscuro" to "blue_dark",
    "azul cielo" to "blue_sky", "rojo oscuro" to "red_dark", "verde claro" to "green_light",
    "verde oscuro" to "green_dark", "marron oscuro" to "brown_dark", "marron claro" to "brown_light",
    "blanco" to "white", "blanca" to "white", "white" to "white", "marfil" to "ivory",
    "plata" to "silver", "plateado" to "silver", "plateada" to "silver", "silver" to "silver",
    "gris" to "grey", "grey" to "grey", "gray" to "grey", "grafito" to "graphite", "antracita" to "anthracite",
    "negro" to "black", "negra" to "black", "black" to "black",
    "azul" to "blue", "blue" to "blue", "turquesa" to "turquoise", "cian" to "cyan",
    "rojo" to "red", "roja" to "red", "red" to "red", "granate" to "burgundy", "burdeos" to "burgundy",
    "verde" to "green", "green" to "green", "oliva" to "olive", "caqui" to "khaki",
    "amarillo" to "yellow", "amarilla" to "yellow", "yellow" to "yellow", "dorado" to "gold", "dorada" to "gold",
    "champan" to "champagne", "naranja" to "orange", "orange" to "orange", "cobre" to "copper",
    "marron" to "brown", "brown" to "brown", "beige" to "beige", "arena" to "sand", "bronce" to "bronze",
    "morado" to "purple", "morada" to "purple", "purple" to "purple", "violeta" to "violet", "lila" to "lavender",
    "rosa" to "pink", "pink" to "pink", "magenta" to "magenta",
).sortedByDescending { it.first.length }

private val METALLIC_WORDS = listOf("metalizado", "metalizada", "metalico", "metalica", "metallic")

/** "gris" -> "grey", "negro metalizado" -> "black_metallic"; null when no color word is found. */
fun colorIdFromText(raw: String): String? {
    val text = normalizeText(raw.trim())
    if (text.isEmpty()) return null
    if (text.replace(' ', '_') in KNOWN_COLOR_IDS) return text.replace(' ', '_')
    val base = COLOR_WORDS.firstOrNull { (word, _) -> Regex("\\b$word\\b").containsMatchIn(text) }?.second
        ?: return null
    val metallic = METALLIC_WORDS.any { it in text }
    return if (metallic && "${base}_metallic" in KNOWN_COLOR_IDS) "${base}_metallic" else base
}

internal val KNOWN_COLOR_IDS = setOf(
    "white", "white_pearl", "ivory", "silver", "silver_metallic", "grey", "grey_dark", "graphite",
    "anthracite", "black", "black_metallic", "blue", "blue_light", "blue_dark", "blue_navy",
    "blue_metallic", "blue_sky", "turquoise", "cyan", "red", "red_dark", "red_metallic", "burgundy",
    "maroon", "cherry", "green", "green_light", "green_dark", "green_metallic", "olive", "khaki", "teal",
    "yellow", "yellow_bright", "gold", "gold_metallic", "champagne", "orange", "orange_dark",
    "orange_metallic", "copper", "brown", "brown_dark", "brown_light", "beige", "sand", "bronze",
    "purple", "purple_dark", "violet", "lavender", "plum", "pink", "pink_light", "magenta",
)

/** "cambio de aceite" -> "Cambio de aceite". */
internal fun String.capitalizeFirst(): String = replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
